import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import FileUpload from './components/FileUpload';
import FileMessage from './components/FileMessage';
import InquiryForm from './components/InquiryForm';
import VideoCall from './components/VideoCall';
import type { VideoCallState } from './components/VideoCall';
import { linkifyText } from './utils/linkify';

interface Message {
  id: string;
  type: 'user' | 'bot' | 'system' | 'operator' | 'auto_message' | 'file';
  text: string;
  timestamp: Date;
  senderName?: string;
  senderAvatar?: string;
  source?: string;
  file?: any;
}

interface SoundNotificationConfig {
  mode: 'always' | 'inactive' | 'off';
  volume: number;
}

// チャット未オープン時に届いたメッセージの見せ方（サイト設定。chat_display_configで受信）
type ClosedMessageDisplayMode = 'badge' | 'preview' | 'auto_open';

interface ChatDisplayConfig {
  displayName: string;
  displayAvatar: string;
  operatorDisplayMode: 'default' | 'operator';
  themeColor: string;
  closedMessageDisplay: ClosedMessageDisplayMode;
}

type SupportStatus = {
  available: boolean;
  availableOperators: number;
};

export function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isTyping, setIsTyping] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [operatorTyping, setOperatorTyping] = useState(false);
  const [humanMode, setHumanMode] = useState(false);
  const [_currentMode, _setCurrentMode] = useState<'ai' | 'human' | 'hybrid'>('ai');
  const [showFileUpload, setShowFileUpload] = useState(false);
  
  // 送信待ちのファイル
  const [pendingFile, setPendingFile] = useState<any>(null);
  
  // 音声録音
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 音声通知設定
  const [notificationConfig, setNotificationConfig] = useState<SoundNotificationConfig>({
    mode: 'off',
    volume: 80
  });
  const [chatDisplayConfig, setChatDisplayConfig] = useState<ChatDisplayConfig>({
    displayName: 'カスタマーサポート',
    displayAvatar: '👨‍💼',
    operatorDisplayMode: 'default',
    themeColor: '#3b82f6',
    closedMessageDisplay: 'preview'
  });
  const [supportStatus, setSupportStatus] = useState<SupportStatus | null>(null);
  // 不在時モード設定
  const [absentModeConfig, setAbsentModeConfig] = useState<{
    absentMode: 'ai_with_form' | 'form_only';
    absentMessage: string;
  }>({ absentMode: 'ai_with_form', absentMessage: 'ただいま担当者が不在です。' });
  // 問い合わせフォーム表示制御
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isWindowActive, setIsWindowActive] = useState(true);
  const [handoverRequested, setHandoverRequested] = useState(false);

  // ビデオ通話
  const [videoCallState, setVideoCallState] = useState<VideoCallState>('idle');
  const [videoCallEnabled, setVideoCallEnabled] = useState(false);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [remoteCameraOn, setRemoteCameraOn] = useState(true);
  const [remoteMicOn, setRemoteMicOn] = useState(true);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const pendingIceCandidatesRef = useRef<any[]>([]);

  // 未読カウント/オープン状態の管理。
  // ws.onmessage やイベントリスナーは deps=[] の useEffect 内クロージャで動くため、
  // state だと初期値しか見えない（staleクロージャ）。描画にも使わないので ref で持つ。
  const unreadCountRef = useRef(0);
  const isChatOpenRef = useRef(false);
  const isWindowActiveRef = useRef(true);
  const closedMessageDisplayRef = useRef<ClosedMessageDisplayMode>('preview');

  const wsRef = useRef<WebSocket | null>(null);
  // WS接続前(CONNECTING)に親から届いた session_event 等を保持し、onopen後に flush する。
  // これが無いと、チャットを開いた直後の chat_open が破棄され、開始トリガー型シナリオが発火しない。
  const pendingWsMessagesRef = useRef<any[]>([]);
  const activeStreamIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Get URL parameters
  const params = new URLSearchParams(window.location.search);
  const site = params.get('site') || 'devsite';
  const sid = params.get('sid') || 'test-session';
  const lang = params.get('lang') || 'ja';
  const apiBaseUrlFromQuery = params.get('apiBaseUrl') || '';
  const apiBaseUrl = (apiBaseUrlFromQuery.trim() || import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000').replace(/\/+$/, '');
  
  // デバッグ: URLパラメータを確認
  console.log('🌐 Widget App URL params:', {
    fullUrl: window.location.href,
    search: window.location.search,
    site: site,
    sid: sid,
    lang: lang
  });
  
  // 言語設定（動的に変更可能）- 初期値をURLパラメータから設定
  const [currentLang, setCurrentLang] = useState<string>(lang);
  
  console.log('🌐 Initial currentLang set to:', lang);

  // ✅ 音声autoplay対策: コンポーネントマウント時に準備
  useEffect(() => {
    const prepareAudio = async () => {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/sounds/default.mp3');
        }
        audioRef.current.volume = 0.01;  // ほぼ無音で再生
        await audioRef.current.play();
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 0.8;  // 通常の音量に戻す
        console.log('✅ Audio prepared for autoplay');
      } catch (err) {
        console.log('⚠️ Autoplay preparation failed (will work after user interaction):', err);
      }
    };
    
    prepareAudio();
  }, []);

  // WSがOPENなら即送信、まだ繋がっていなければキューに積んで onopen 後に flush する
  const sendOrQueueWs = (payload: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    } else {
      pendingWsMessagesRef.current.push(payload);
    }
  };

  // 親ウィンドウからのメッセージを受信
  useEffect(() => {
    const handleParentMessage = (event: MessageEvent) => {
      // チャットオープン通知
      if (event.data.type === 'CHAT_OPENED') {
        console.log('📂 Chat opened - resetting unread count');
        isChatOpenRef.current = true;
        unreadCountRef.current = 0;
        window.parent?.postMessage({
          type: 'chatbot-unread',
          count: 0
        }, '*');
      }

      // チャットクローズ通知（閉じた後に届くメッセージを未読/プレビュー扱いにするため）
      if (event.data.type === 'CHAT_CLOSED') {
        isChatOpenRef.current = false;
      }

      // 言語変更
      if (event.data.type === 'CHANGE_LANGUAGE') {
        const newLang = event.data.lang;
        console.log('🌐 Widget App: Language change received:', newLang);
        setCurrentLang(newLang);
        
        // WebSocketに言語変更を通知
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'change_language',
            lang: newLang
          }));
        }
      }

      // 親（埋め込みスクリプト）からのセッションイベントをWebSocketへ中継
      // WS未接続時(チャットを開いた直後など)はキューして接続後に送る。
      if (event.data.type === 'SESSION_EVENT') {
        const { eventType, data } = event.data;
        sendOrQueueWs({
          type: 'session_event',
          eventType,
          data
        });
      }
      
    };

    window.addEventListener('message', handleParentMessage);

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, []);

  // メッセージ/入力中表示が更新されたら自動スクロール
  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        // ストリーミング中はinstantスクロール（ブレ防止）
        if (activeStreamIdRef.current) {
          container.scrollTop = container.scrollHeight;
        } else {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages, botTyping, operatorTyping]);

  // ウィンドウのアクティブ状態を監視
  useEffect(() => {
    let hasBecomesInactive = false;  // 重複送信を防止
    
    const handleFocus = () => {
      setIsWindowActive(true);
      isWindowActiveRef.current = true;
      hasBecomesInactive = false;

      // ✅ 未読カウントをクリア（deps=[]のクロージャなのでstateではなくrefで判定する）
      if (isChatOpenRef.current && unreadCountRef.current > 0) {
        unreadCountRef.current = 0;
        window.parent?.postMessage({
          type: 'chatbot-unread',
          count: 0
        }, '*');
      }

      // ✅ アクティブに戻ったことをサーバーに通知（タイマーキャンセル）
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'user_active'
        }));
      }
    };
    
    const handleBlur = () => {
      setIsWindowActive(false);
      isWindowActiveRef.current = false;

      // ✅ 重複送信を防止
      if (hasBecomesInactive) return;
      hasBecomesInactive = true;
      
      // 非アクティブになったことをサーバーに通知
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'user_inactive'
        }));
      }
    };
    
    const handleVisibilityChange = () => {
      const isActive = !document.hidden;
      setIsWindowActive(isActive);
      isWindowActiveRef.current = isActive;

      if (isActive) {
        handleFocus();
      } else {
        handleBlur();
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // チャットが閉じている/ウィンドウが非アクティブな時に届いたメッセージの通知処理。
  // 未読カウントを増やして親（埋め込みスクリプト）へ伝え、チャット未オープン時は
  // サイト設定（closedMessageDisplay）に応じてプレビュー吹き出し or 自動オープンを親にリクエストする。
  const notifyMessageWhileInactive = (message: Message) => {
    const chatOpen = isChatOpenRef.current;
    if (chatOpen && isWindowActiveRef.current) return;

    unreadCountRef.current += 1;
    window.parent?.postMessage({
      type: 'chatbot-unread',
      count: unreadCountRef.current
    }, '*');

    // プレビュー/自動オープンは「チャット未オープン」時のみ
    // （開いたまま非アクティブの場合は従来どおりバッジと音のみ）
    if (chatOpen) return;

    const mode = closedMessageDisplayRef.current;
    if (mode === 'preview') {
      window.parent?.postMessage({
        type: 'chatbot-preview',
        message: {
          id: message.id,
          text: message.text,
          senderName: message.senderName || '',
          senderAvatar: message.senderAvatar || ''
        }
      }, '*');
    } else if (mode === 'auto_open') {
      window.parent?.postMessage({ type: 'chatbot-request-open' }, '*');
    }
  };

  // 音声通知を再生する関数
  const playNotificationSound = (messageType: 'bot' | 'operator' | 'auto_message') => {
    // 最新の設定を取得（stateではなくlocalStorageから）
    const storedConfig = localStorage.getItem('notificationConfig');
    const config = storedConfig ? JSON.parse(storedConfig) : notificationConfig;
    
    console.log('🔔 playNotificationSound called:', {
      messageType,
      mode: config.mode,
      volume: config.volume,
      isWindowActive
    });

    // 通知判定
    const shouldNotify = 
      config.mode === 'always' || 
      (config.mode === 'inactive' && !isWindowActive);

    console.log('🔔 shouldNotify:', shouldNotify);

    if (!shouldNotify || config.mode === 'off') {
      console.log('🔕 Notification skipped:', { shouldNotify, mode: config.mode });
      return;
    }

    try {
      const soundUrl = '/sounds/default.mp3';
      console.log('🔊 Playing sound:', soundUrl);
      
      // 最新の設定を使用
      const storedConfig = localStorage.getItem('notificationConfig');
      const config = storedConfig ? JSON.parse(storedConfig) : notificationConfig;
      
      if (!audioRef.current) {
        audioRef.current = new Audio(soundUrl);
      } else {
        audioRef.current.src = soundUrl;
      }
      
      audioRef.current.volume = config.volume / 100;
      audioRef.current.play()
        .then(() => {
          console.log('✅ Sound played successfully');
        })
        .catch(err => {
          console.warn('❌ Failed to play notification sound:', err);
        });
    } catch (error) {
      console.error('❌ Notification sound error:', error);
    }
  };

  // WebSocket connection
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout: NodeJS.Timeout;
    
    const connect = () => {
      if (!isMounted) return;
      
      const wsProtocol = apiBaseUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = apiBaseUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}://${wsHost}/chat?site=${site}&sid=${sid}&lang=${currentLang}`;

      console.log('🔌 API Base URL:', apiBaseUrl);
      console.log('🔌 Connecting to:', wsUrl, `(attempt ${retryCount + 1})`);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMounted) return;
        console.log('WebSocket connected successfully');
        setIsConnected(true);
        setConnectionStatus('connected');
        retryCount = 0;

        // 接続前にキューした session_event 等を順番に flush（チャットを開いた直後の chat_open を取りこぼさない）
        if (pendingWsMessagesRef.current.length > 0) {
          const queued = pendingWsMessagesRef.current;
          pendingWsMessagesRef.current = [];
          for (const payload of queued) {
            try {
              ws.send(JSON.stringify(payload));
            } catch (e) {
              console.warn('Failed to flush queued WS message:', e);
            }
          }
        }

        // Notify parent
        window.parent?.postMessage({ type: 'chatbot-ready' }, '*');
      };

      ws.onmessage = (event) => {
        if (!isMounted) return;
        console.log('Received message:', event.data);
        try {
          const data = JSON.parse(event.data);
          const storedChatDisplay = localStorage.getItem('chatDisplayConfig');
          const effectiveChatDisplay: ChatDisplayConfig = storedChatDisplay
            ? JSON.parse(storedChatDisplay)
            : chatDisplayConfig;

          // ✅ 在席/退席（support_status）はメッセージとして表示しない
          if (data?.type === 'support_status') {
            const next: SupportStatus = {
              available: Boolean(data.available),
              availableOperators: Number.isFinite(Number(data.availableOperators)) ? Number(data.availableOperators) : 0
            };
            setSupportStatus(next);
            // form_onlyモードで不在の場合、問い合わせフォームを自動表示
            if (!next.available) {
              setAbsentModeConfig(prev => {
                if (prev.absentMode === 'form_only') {
                  setShowInquiryForm(true);
                }
                return prev;
              });
            } else {
              // オペレーターが復帰したらフォームを非表示（未送信の場合）
              setInquirySubmitted(submitted => {
                if (!submitted) setShowInquiryForm(false);
                return submitted;
              });
            }
            return;
          }
          
          // 会話ID通知
          if (data.type === 'conversation_info') {
            setCurrentConversationId(data.conversationId || null);
            return;
          }

          // 音声通知設定の受信
          if (data.type === 'notification_config') {
            const newConfig = {
              mode: data.mode || 'off',
              volume: data.volume || 80
            };
            setNotificationConfig(newConfig);
            // localStorageにも保存（即座に反映させるため）
            localStorage.setItem('notificationConfig', JSON.stringify(newConfig));
            console.log('🔔 Sound notification config received and saved:', data.mode, data.volume);
            return;
          }

          // チャット表示（送信者表示）設定の受信
          if (data.type === 'chat_display_config') {
            const closedMessageDisplay: ClosedMessageDisplayMode =
              data.closedMessageDisplay === 'badge' || data.closedMessageDisplay === 'auto_open'
                ? data.closedMessageDisplay
                : 'preview';
            const cfg: ChatDisplayConfig = {
              displayName: data.displayName || 'カスタマーサポート',
              displayAvatar: data.displayAvatar || '👨‍💼',
              operatorDisplayMode: data.operatorDisplayMode === 'operator' ? 'operator' : 'default',
              themeColor: data.themeColor || '#3b82f6',
              closedMessageDisplay
            };
            setChatDisplayConfig(cfg);
            closedMessageDisplayRef.current = closedMessageDisplay;
            localStorage.setItem('chatDisplayConfig', JSON.stringify(cfg));
            return;
          }

          // 不在時モード設定の受信
          if (data.type === 'absent_mode_config') {
            setAbsentModeConfig({
              absentMode: data.absentMode || 'ai_with_form',
              absentMessage: data.absentMessage || 'ただいま担当者が不在です。'
            });
            return;
          }

          // ビデオ通話設定
          if (data.type === 'video_call_config') {
            setVideoCallEnabled(Boolean(data.enabled));
            return;
          }

          // ビデオ通話: リクエスト確認
          if (data.type === 'video_call_request_ack') {
            setActiveCallId(data.callId);
            setVideoCallState('requesting');
            return;
          }

          // ビデオ通話: オペレーターからのSDP offer
          if (data.type === 'video_call_offer') {
            handleVideoCallOffer(data);
            return;
          }

          // ビデオ通話: ICE candidate
          if (data.type === 'video_call_ice') {
            if (data.candidate) {
              if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
                peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
              } else {
                // PeerConnectionがまだ準備できていない場合はバッファリング
                pendingIceCandidatesRef.current.push(data.candidate);
              }
            }
            return;
          }

          // ビデオ通話: 終了/辞退
          if (data.type === 'video_call_ended' || data.type === 'video_call_declined') {
            cleanupVideoCall();
            if (data.type === 'video_call_declined' && data.message) {
              const msg: Message = {
                id: Math.random().toString(36).substring(2),
                type: 'system',
                text: data.message,
                timestamp: new Date(),
                source: 'video_call'
              };
              setMessages(prev => [...prev, msg]);
            }
            return;
          }

          // ビデオ通話: 相手のメディアトグル
          if (data.type === 'video_call_media_toggle') {
            if (data.camera !== undefined) setRemoteCameraOn(Boolean(data.camera));
            if (data.mic !== undefined) setRemoteMicOn(Boolean(data.mic));
            return;
          }

          // 問い合わせフォームトリガー（ナレッジギャップ or form_only不在）
          if (data.type === 'inquiry_form_trigger') {
            setShowInquiryForm(true);
            setBotTyping(false);
            // form_only不在の場合のみシステムメッセージを追加
            // knowledge_gapの場合はbot_stream_replaceで既にAI回答が定型文に置換済み
            if (data.reason === 'form_only_absent' && data.message) {
              const msg: Message = {
                id: Math.random().toString(36).substring(2),
                type: 'system',
                text: data.message,
                timestamp: new Date(),
                source: 'inquiry_trigger'
              };
              setMessages(prev => [...prev, msg]);
            }
            return;
          }

          if (data.type === 'handover_denied') {
            setHandoverRequested(false);
            setHumanMode(false);
            if (typeof data?.text === 'string') {
              const message: Message = {
                id: Math.random().toString(36).substring(2),
                type: 'system',
                text: data.text,
                timestamp: new Date(),
                source: 'handover'
              };
              setMessages(prev => [...prev, message]);
            }
            return;
          }

          if (data.type === 'handover_accepted') {
            setHandoverRequested(true);
            setHumanMode(true);
            if (typeof data?.text === 'string') {
              const message: Message = {
                id: Math.random().toString(36).substring(2),
                type: 'system',
                text: data.text,
                timestamp: new Date(),
                source: 'handover'
              };
              setMessages(prev => [...prev, message]);
            }
            return;
          }
          
          if (data.type === 'operator_typing') {
            setOperatorTyping(Boolean(data.isTyping));
            setHumanMode(true);
            return;
          }

          if (data.type === 'bot_stream_start') {
            const streamId = data.id || `bot_${Date.now()}`;
            activeStreamIdRef.current = streamId;
            // 入力中表示は最初のdeltaが届くまで維持する（ここではfalseにしない）
            return;
          }

          if (data.type === 'bot_stream_delta') {
            // 最初のdeltaが届いたら入力中表示を消す
            setBotTyping(false);
            const streamId = data.id || activeStreamIdRef.current;
            if (streamId && typeof data.delta === 'string') {
              setMessages(prev => {
                const existing = prev.find(msg => msg.id === streamId);
                if (existing) {
                  // 既にメッセージがある → テキスト追加
                  return prev.map(msg => (
                    msg.id === streamId
                      ? { ...msg, text: (msg.text || '') + data.delta }
                      : msg
                  ));
                }
                // 初回delta → メッセージを新規作成（空バブルにならない）
                return [...prev, {
                  id: streamId,
                  type: 'bot' as const,
                  text: data.delta,
                  timestamp: new Date(),
                  senderName: effectiveChatDisplay.displayName,
                  senderAvatar: effectiveChatDisplay.displayAvatar,
                  source: 'ai' as const
                }];
              });
            }
            return;
          }

          if (data.type === 'bot_stream_end') {
            if (activeStreamIdRef.current === data.id) {
              activeStreamIdRef.current = null;
            }
            setBotTyping(false);
            return;
          }

          // ナレッジギャップ検出時: ストリーミング済みのAI回答を定型文に置き換え
          if (data.type === 'bot_stream_replace') {
            const streamId = data.id;
            if (streamId) {
              setMessages(prev => prev.map(msg =>
                msg.id === streamId ? { ...msg, text: data.text } : msg
              ));
            }
            if (activeStreamIdRef.current === streamId) {
              activeStreamIdRef.current = null;
            }
            return;
          }

          // 非ストリーミングのAI回答を定型文に置き換え
          if (data.type === 'bot_replace') {
            setMessages(prev => {
              const lastBotIdx = [...prev].reverse().findIndex(msg => msg.type === 'bot');
              if (lastBotIdx === -1) return prev;
              const idx = prev.length - 1 - lastBotIdx;
              return prev.map((msg, i) => i === idx ? { ...msg, text: data.text } : msg);
            });
            return;
          }

          // 何か受信したら「返答待ち」を解除（エラー/ボット/オペ/自動メッセ含む）
          if (data?.type && data.type !== 'conversation_restored') {
            setBotTyping(false);
          }
          
          if (data.type === 'operator') {
            // オペレーターメッセージ
            setHumanMode(true);
            setOperatorTyping(false);
            const message: Message = {
              id: Math.random().toString(36).substring(2),
              type: 'operator',
              text: data.text,
              timestamp: new Date(),
              senderName: data.operatorName || effectiveChatDisplay.displayName,
              senderAvatar: data.operatorAvatar || effectiveChatDisplay.displayAvatar,
              source: 'human'
            };
            setMessages(prev => [...prev, message]);

            // ✅ チャットが閉じているか非アクティブな場合は未読/プレビュー/自動オープン通知
            notifyMessageWhileInactive(message);

            // 音声通知を再生
            playNotificationSound('operator');
          } else if (data.type === 'auto_message') {
            // 自動メッセージ
            const message: Message = {
              id: Math.random().toString(36).substring(2),
              type: 'auto_message',
              text: data.text,
              timestamp: new Date(),
              senderName: effectiveChatDisplay.displayName,
              senderAvatar: effectiveChatDisplay.displayAvatar,
              source: 'auto'
            };
            setMessages(prev => [...prev, message]);

            // ✅ チャットが閉じているか非アクティブな場合は未読/プレビュー/自動オープン通知
            notifyMessageWhileInactive(message);

            // 音声通知を再生
            playNotificationSound('auto_message');
          } else if (data.type === 'bot') {
            // AIボットメッセージ
            if (typeof data?.text === 'string') {
              if (data.text.includes('会話モードが「AI対応」')) setHumanMode(false);
              if (data.text.includes('会話モードが「人間対応」')) setHumanMode(true);
            }
            const message: Message = {
              id: Math.random().toString(36).substring(2),
              type: 'bot',
              text: data.text,
              timestamp: new Date(),
              senderName: effectiveChatDisplay.displayName,
              senderAvatar: effectiveChatDisplay.displayAvatar,
              source: data.source
            };
            setMessages(prev => [...prev, message]);

            // ✅ チャットが閉じているか非アクティブな場合は未読/プレビュー/自動オープン通知
            notifyMessageWhileInactive(message);

            // 音声通知を再生
            playNotificationSound('bot');
          } else if (data.type === 'conversation_restored') {
            // 会話履歴復元
            console.log('📚 Restoring conversation history:', data.messages.length, 'messages');
            
            const restoredMessages: Message[] = data.messages.map((msg: any) => ({
              id: msg.id,
              type: msg.role === 'user' ? 'user' : msg.role === 'operator' ? 'operator' : 'bot',
              text: typeof msg.text === 'string' ? msg.text : '',
              timestamp: new Date(msg.createdAt),
              // operatorモード時はサーバが解決した実オペレーター名を使う（ライブ送信と一致）。
              // 送られない場合（default モード/AI/bot）は共通displayNameにフォールバック。
              senderName: msg.role === 'user'
                ? undefined
                : (msg.operatorName || effectiveChatDisplay.displayName),
              senderAvatar: msg.role === 'user'
                ? undefined
                : (msg.operatorAvatar || effectiveChatDisplay.displayAvatar),
              source: msg.role === 'bot' ? 'ai' : msg.role,
              file: msg.fileUrl ? {
                id: msg.id,
                originalName: typeof msg.text === 'string' && msg.text.includes('ファイルを送信:')
                  ? msg.text.replace('ファイルを送信: ', '')
                  : 'ファイル',
                url: msg.fileUrl,
                mimeType: msg.fileType,
                fileSize: 0
              } : undefined
            }));

            setMessages(restoredMessages);
          } else if (data.type === 'file_message') {
            // ファイルメッセージ
            const message: Message = {
              id: Math.random().toString(36).substring(2),
              type: 'file',
              text: data.text || 'ファイルが送信されました',
              timestamp: new Date(),
              file: data.file
            };
            setMessages(prev => [...prev, message]);

            // ✅ チャットが閉じているか非アクティブな場合は未読/プレビュー/自動オープン通知
            notifyMessageWhileInactive(message);
          } else {
            // 通常のメッセージ（想定外イベントは無視）
            if (typeof data?.text !== 'string') {
              return;
            }
            if (data.type === 'system' && data.text.includes('オペレーター')) {
              setHumanMode(true);
            }
            const message: Message = {
              id: Math.random().toString(36).substring(2),
              type: data.type,
              text: data.text,
              timestamp: new Date(),
              senderName: data.type === 'user' ? undefined : effectiveChatDisplay.displayName,
              senderAvatar: data.type === 'user' ? undefined : effectiveChatDisplay.displayAvatar,
              source: data.source
            };
            setMessages(prev => [...prev, message]);
          }
        } catch (error) {
          console.error('Parse error:', error);
          setBotTyping(false);
        }
      };

      ws.onclose = (event) => {
        if (!isMounted) return;
        console.log('WebSocket disconnected. Code:', event.code, 'Reason:', event.reason);
        setIsConnected(false);
        setBotTyping(false);

        // Only retry if it's an unexpected closure and we haven't exceeded max retries
        if (event.code !== 1000 && retryCount < maxRetries && isMounted) {
          setConnectionStatus('connecting');
          retryCount++;
          retryTimeout = setTimeout(() => {
            if (isMounted) connect();
          }, 2000);
        } else {
          setConnectionStatus('disconnected');
        }
      };

      ws.onerror = (error) => {
        if (!isMounted) return;
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        setIsConnected(false);
        setBotTyping(false);
      };
    };

    connect();

    return () => {
      isMounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []);

  // 入力中状態の管理
  const handleInputChange = (e: any) => {
    const value = e.target.value;
    setInputValue(value);
    
    // 入力中状態の送信
    if (!isTyping && value.length > 0) {
      setIsTyping(true);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'typing_start' }));
      }
    }
    
    // タイピング停止のタイマー
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing_stop' }));
        }
      }
    }, 1000);
  };

  const handleInputBlur = (e: any) => {
    (e.target as HTMLElement).style.borderColor = '#e5e7eb';
    (e.target as HTMLElement).style.boxShadow = 'none';
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTyping) {
      setIsTyping(false);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'typing_stop' }));
      }
    }
  };

  const requestOperator = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (!supportStatus?.available || handoverRequested) return;
    const ok = window.confirm('直接オペレーターとお話ししますか？');
    if (!ok) return;

    setHandoverRequested(true);
    wsRef.current.send(JSON.stringify({ type: 'handover_request' }));
    // サーバーからのシステムメッセージで案内する
  };

  // ============= ビデオ通話機能 =============

  const requestVideoCall = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    if (videoCallState !== 'idle') return;
    setVideoCallState('requesting');
    wsRef.current.send(JSON.stringify({ type: 'video_call_request' }));
  }, [videoCallState]);

  const handleVideoCallOffer = useCallback(async (data: any) => {
    try {
      setVideoCallState('connecting');

      // Get local media
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // デフォルトでカメラオフ・音声オン
      stream.getVideoTracks().forEach(track => { track.enabled = false; });
      setLocalStream(stream);
      setCameraOn(false);
      setMicOn(true);

      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: data.iceServers || [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      peerConnectionRef.current = pc;

      // Add local tracks
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Handle remote tracks
      pc.ontrack = (event) => {
        console.log('📹 Widget ontrack:', event.track.kind, 'streams:', event.streams.length);
        if (event.streams[0]) {
          setRemoteStream(event.streams[0]);
        } else {
          // Fallback: create stream from track
          setRemoteStream((prev) => {
            const s = prev ?? new MediaStream();
            s.addTrack(event.track);
            return s;
          });
        }
      };

      // ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'video_call_ice',
            callId: data.callId,
            candidate: event.candidate.toJSON()
          }));
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('📹 Widget PC connectionState:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          setVideoCallState('active');
        } else if (pc.connectionState === 'failed') {
          cleanupVideoCall();
        }
        // Note: 'disconnected' is transient and can recover — do NOT clean up on it
      };

      pc.oniceconnectionstatechange = () => {
        console.log('📹 Widget PC iceConnectionState:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          setVideoCallState('active');
        }
      };

      // Set remote description (operator's offer)
      console.log('📹 Widget: setting remote description (operator offer)');
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));

      // Flush buffered ICE candidates that arrived before PeerConnection was ready
      if (pendingIceCandidatesRef.current.length > 0) {
        console.log(`📹 Widget: flushing ${pendingIceCandidatesRef.current.length} buffered ICE candidates`);
        for (const candidate of pendingIceCandidatesRef.current) {
          pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
        }
        pendingIceCandidatesRef.current = [];
      }

      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('📹 Widget: sending SDP answer to operator');

      // Send answer to server
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'video_call_answer',
          callId: data.callId,
          sdp: answer
        }));
      }

      setActiveCallId(data.callId);
      // Don't set 'active' here — onconnectionstatechange will set it when connected
    } catch (err) {
      console.error('Video call setup error:', err);
      cleanupVideoCall();
      const msg: Message = {
        id: Math.random().toString(36).substring(2),
        type: 'system',
        text: 'ビデオ通話の接続に失敗しました。カメラ・マイクの許可を確認してください。',
        timestamp: new Date(),
        source: 'video_call'
      };
      setMessages(prev => [...prev, msg]);
    }
  }, []);

  const toggleCamera = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCameraOn(videoTrack.enabled);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'video_call_media_toggle',
            callId: activeCallId,
            camera: videoTrack.enabled,
            mic: micOn
          }));
        }
      }
    }
  }, [localStream, activeCallId, micOn]);

  const toggleMic = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicOn(audioTrack.enabled);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'video_call_media_toggle',
            callId: activeCallId,
            camera: cameraOn,
            mic: audioTrack.enabled
          }));
        }
      }
    }
  }, [localStream, activeCallId, cameraOn]);

  const hangupVideoCall = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'video_call_hangup',
        callId: activeCallId
      }));
    }
    cleanupVideoCall();
  }, [activeCallId]);

  const cleanupVideoCall = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    setLocalStream((prev) => {
      if (prev) prev.getTracks().forEach(track => track.stop());
      return null;
    });
    setRemoteStream((prev) => {
      if (prev) prev.getTracks().forEach(track => track.stop());
      return null;
    });
    pendingIceCandidatesRef.current = [];
    setVideoCallState('idle');
    setActiveCallId(null);
    setCameraOn(true);
    setMicOn(true);
    setRemoteCameraOn(true);
    setRemoteMicOn(true);
  }, []);

  const sendMessage = async () => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;

    // テキストメッセージ、ファイル、または録音音声のいずれかが必要
    if (!inputValue.trim() && !pendingFile && !recordedAudio) return;

    // 録音音声がある場合、先にアップロード
    if (recordedAudio) {
      try {
        const formData = new FormData();
        const audioFile = new File([recordedAudio], `recording_${Date.now()}.webm`, {
          type: 'audio/webm'
        });
        formData.append('file', audioFile);

      // ✅ CDN側から渡される apiBaseUrl を優先
        const response = await fetch(`${apiBaseUrl}/api/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ 音声アップロード成功:', data);
          
          // 音声メッセージをUIに追加
          const audioMessage: Message = {
            id: Math.random().toString(36).substring(2),
            type: 'file',
            text: '🎙️ 音声メッセージを送信しました',
            timestamp: new Date(),
            file: data.file
          };
          setMessages(prev => [...prev, audioMessage]);

          // WebSocket経由でファイル情報を送信
          wsRef.current.send(JSON.stringify({
            type: 'file',
            fileId: data.file.id,
            fileName: data.file.originalName,
            fileUrl: data.file.url,
            fileType: data.file.mimeType,
            fileSize: data.file.fileSize
          }));

          setRecordedAudio(null);
          setRecordingDuration(0);
        } else {
          const errorData = await response.json();
          console.error('❌ 音声アップロード失敗:', response.status, errorData);
          alert(`音声の送信に失敗しました: ${errorData.error || response.statusText}`);
          setRecordedAudio(null);
          setRecordingDuration(0);
        }
      } catch (error) {
        console.error('音声アップロードエラー:', error);
        alert('音声の送信に失敗しました。');
        setRecordedAudio(null);
        setRecordingDuration(0);
      }
    }

    // テキストメッセージがある場合
    if (inputValue.trim()) {
      const messageText = inputValue.trim();
      
      // Add user message to UI immediately
      const userMessage: Message = {
        id: Math.random().toString(36).substring(2),
        type: 'user',
        text: messageText,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      
      // Send to server
      const message = {
        type: 'user',
        text: messageText,
      };
      
      console.log('Sending message:', message);
      wsRef.current.send(JSON.stringify(message));
      if (!humanMode && !handoverRequested) {
        setBotTyping(true);
      }
      setInputValue('');
    }

    // ファイルがある場合
    if (pendingFile) {
      // ファイルメッセージをUIに追加
      const fileMessage: Message = {
        id: Math.random().toString(36).substring(2),
        type: 'file',
        text: `${pendingFile.originalName}を送信しました`,
        timestamp: new Date(),
        file: pendingFile
      };
      setMessages(prev => [...prev, fileMessage]);

      // WebSocket経由でファイル情報を送信
      wsRef.current.send(JSON.stringify({
        type: 'file',
        fileId: pendingFile.id,
        fileName: pendingFile.originalName,
        fileUrl: pendingFile.url,
        fileType: pendingFile.mimeType,
        fileSize: pendingFile.fileSize
      }));
      if (!humanMode && !handoverRequested) {
        setBotTyping(true);
      }

      setPendingFile(null);
    }
    
    // 入力中状態をリセット
    setIsTyping(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleFileUploaded = (fileInfo: any) => {
    // ファイルを送信待ちとして保存（即座に送信しない）
    setPendingFile(fileInfo);
    setShowFileUpload(false);
    console.log('📎 File uploaded and ready to send:', fileInfo.originalName);
  };

  // 音声録音開始
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // 録音時間をカウント
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      console.log('🎙️ Recording started');
    } catch (error) {
      console.error('録音開始エラー:', error);
      alert('マイクへのアクセスが拒否されました。ブラウザの設定を確認してください。');
    }
  };

  // 音声録音停止
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      console.log('🎙️ Recording stopped');
    }
  };

  // 録音音声を削除
  const cancelRecording = () => {
    setRecordedAudio(null);
    setRecordingDuration(0);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  const handleKeyPress = (e: any) => {
    // Check if it's a composition event (Japanese input)
    if (e.isComposing || e.keyCode === 229) {
      return; // Don't send during composition
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCompositionEnd = (e: any) => {
    // Handle composition end for Japanese input
    if (e.type === 'compositionend') {
      setInputValue(e.target.value);
    }
  };

  // お客様には「AI/オペレーター/自動メッセージ」を区別させない方針。
  // ヘッダはサイト側の表示名のみを出す。
  const getModeLabel = () => chatDisplayConfig.displayName;
  const getModeColor = () => chatDisplayConfig.themeColor || '#3b82f6';

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isAvatarUrl = (value: string) => /^(https?:\/\/|\/|data:image\/)/.test(value);

  const renderAvatar = (avatar?: string, size = 32) => {
    const value = avatar || '👨‍💼';
    if (isAvatarUrl(value)) {
      return (
        <img
          src={value}
          alt=""
          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
        />
      );
    }
    return <span style={{ fontSize: `${Math.round(size / 2)}px` }}>{value}</span>;
  };

  const getMessageIcon = (message: Message) => {
    if (message.type === 'user' || message.type === 'file') return '👨‍💼';
    return message.senderAvatar || chatDisplayConfig.displayAvatar || '👨‍💼';
  };

  const getMessageLabel = (message: Message) => {
    if (message.type === 'user' || message.type === 'file') return '';
    return message.senderName || chatDisplayConfig.displayName || 'カスタマーサポート';
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#ffffff',
      position: 'relative',
    }}>
      {/* Video Call Overlay */}
      <VideoCall
        state={videoCallState}
        localStream={localStream}
        remoteStream={remoteStream}
        cameraOn={cameraOn}
        micOn={micOn}
        remoteCameraOn={remoteCameraOn}
        remoteMicOn={remoteMicOn}
        onToggleCamera={toggleCamera}
        onToggleMic={toggleMic}
        onHangup={hangupVideoCall}
        themeColor={chatDisplayConfig.themeColor}
      />

      {/* Header */}
      <div style={{ 
        background: '#ffffff', 
        borderBottom: '1px solid #e5e7eb',
        color: '#1f2937', 
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {getModeLabel()}
          </h1>
          <div style={{ marginTop: '4px', marginLeft: '40px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {connectionStatus === 'connected' && (
                <>
                  <span>オンライン</span>
                  {supportStatus && (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        background: supportStatus.available ? '#dcfce7' : '#f3f4f6',
                        color: supportStatus.available ? '#166534' : '#6b7280',
                        fontWeight: 600,
                      }}
                    >
                      {supportStatus.available ? '対応可能' : '不在'}
                    </span>
                  )}
                </>
              )}
              {connectionStatus === 'connecting' && '接続中...'}
              {connectionStatus === 'disconnected' && '切断されました'}
              {connectionStatus === 'error' && 'エラー'}
              {operatorTyping && humanMode && (
                <span style={{ color: '#6b7280', fontWeight: '500' }}>
                  • 入力中
                </span>
              )}
            </div>
            {connectionStatus === 'connected' && (
              <button
                onClick={requestOperator}
                disabled={!supportStatus?.available || handoverRequested}
                style={{
                  fontSize: '12px',
                  padding: '6px 10px',
                  borderRadius: '9999px',
                  border: '1px solid #e5e7eb',
                  background: handoverRequested ? '#f3f4f6' : '#ffffff',
                  color: handoverRequested ? '#9ca3af' : '#374151',
                  cursor: (!supportStatus?.available || handoverRequested) ? 'not-allowed' : 'pointer'
                }}
                title={supportStatus?.available ? '直接オペレーターと会話する' : 'ただいま不在です'}
              >
                オペレーターと会話
              </button>
            )}
            {videoCallEnabled && supportStatus?.available && videoCallState === 'idle' && (
              <button
                onClick={requestVideoCall}
                style={{
                  fontSize: '12px',
                  padding: '6px 10px',
                  borderRadius: '9999px',
                  border: '1px solid #e5e7eb',
                  background: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                }}
                title="ビデオ通話をリクエスト"
              >
                📹 ビデオ通話
              </button>
            )}
          </div>
        </div>
        <button 
          onClick={() => window.parent?.postMessage({ type: 'chatbot-close' }, '*')}
          style={{
            background: '#f3f4f6',
            border: 'none',
            color: '#6b7280',
            padding: '8px',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.background = '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.background = '#f3f4f6';
          }}
          title="チャットを閉じる"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div style={{ 
        flex: 1, 
        padding: '20px', 
        overflowY: 'auto',
        background: '#f8fafc'
      }}>
        {messages.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280'
          }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#374151' 
            }}>
              こんにちは！
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: '14px', 
              lineHeight: '1.6',
              color: '#6b7280'
            }}>
              何かお手伝いできることはありますか？<br />
              テキストメッセージやファイルを送信できます。
            </p>
          </div>
        )}
        
        {messages.map((message) => (
          <div 
            key={message.id}
            style={{
              marginBottom: '16px',
              display: 'flex',
              justifyContent: message.type === 'user' || message.type === 'file' ? 'flex-end' : 
                            message.type === 'system' ? 'center' : 'flex-start'
            }}
          >
            {message.type === 'system' ? (
              // システムメッセージ（会話復元通知など）
              <div style={{
                padding: '8px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#3b82f6',
                textAlign: 'center',
                maxWidth: '300px'
              }}>
                📚 {message.text}
              </div>
            ) : (
              <div style={{
                maxWidth: '80%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                flexDirection: message.type === 'user' || message.type === 'file' ? 'row-reverse' : 'row'
              }}>
                {message.type === 'file' ? (
                  // ファイルメッセージの表示
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <FileMessage 
                      file={message.file} 
                      isFromUser={true}
                    />
                    {message.text && message.text !== `${message.file.originalName}を送信しました` && (
                      <div style={{
                        padding: '8px 12px',
                        borderRadius: '12px',
                        background: '#3b82f6',
                        color: 'white',
                        fontSize: '14px',
                        maxWidth: '300px'
                      }}>
                        {message.text}
                      </div>
                    )}
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '2px',
                      textAlign: 'right'
                    }}>
                      {message.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ) : (
                  // 通常のテキストメッセージ
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {/* アイコン横に名前を表示 */}
                    {(message.type !== 'user') && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#111827',
                          flexShrink: 0
                        }}>
                          {renderAvatar(getMessageIcon(message), 32)}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {getMessageLabel(message)}
                        </div>
                      </div>
                    )}

                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '18px',
                      background: message.type === 'user' ? chatDisplayConfig.themeColor : '#ffffff',
                      color: message.type === 'user' ? 'white' : '#1f2937',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      maxWidth: '300px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      border: message.type === 'user' ? 'none' : '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}>
                      <div style={{ whiteSpace: 'pre-wrap', minHeight: '1.5em' }}>
                        {linkifyText(message.text)}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '2px',
                      textAlign: message.type === 'user' ? 'right' : 'left'
                    }}>
                      {message.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Bot typing indicator (waiting for response) */}
        {botTyping && (
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#111827'
              }}>
                {renderAvatar(chatDisplayConfig.displayAvatar, 32)}
              </div>
              <div style={{
                padding: '12px 16px',
                borderRadius: '16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#6b7280'
                }}>
                  <div className="typing-dots">
                    <span>●</span>
                    <span>●</span>
                    <span>●</span>
                  </div>
                  <span style={{ fontSize: '12px' }}>入力中</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Operator typing indicator */}
        {operatorTyping && humanMode && (
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#111827'
              }}>
                {renderAvatar(chatDisplayConfig.displayAvatar, 32)}
              </div>
              <div style={{
                padding: '12px 16px',
                borderRadius: '16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#6b7280'
                }}>
                  <div className="typing-dots">
                    <span>●</span>
                    <span>●</span>
                    <span>●</span>
                  </div>
                  <span style={{ fontSize: '12px' }}>入力中</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 問い合わせフォーム（チャット内埋め込み） */}
        {showInquiryForm && !inquirySubmitted && (
          <InquiryForm
            siteKey={site}
            conversationId={currentConversationId || undefined}
            apiBaseUrl={apiBaseUrl}
            themeColor={chatDisplayConfig.themeColor}
            onSubmitted={() => {
              setInquirySubmitted(true);
              setShowInquiryForm(false);
              const thankMsg: Message = {
                id: Math.random().toString(36).substring(2),
                type: 'system',
                text: '送信ありがとうございました。担当者より回答いたします。',
                timestamp: new Date(),
                source: 'inquiry'
              };
              setMessages(prev => [...prev, thankMsg]);
            }}
            onClose={absentModeConfig.absentMode === 'form_only' ? undefined : () => setShowInquiryForm(false)}
          />
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* File Upload Area */}
      {showFileUpload && (
        <div style={{ 
          padding: '20px', 
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb'
        }}>
          <FileUpload 
            onFileUploaded={handleFileUploaded}
            disabled={!isConnected}
          />
          <div style={{ 
            marginTop: '12px', 
            textAlign: 'center' 
          }}>
            <button
              onClick={() => setShowFileUpload(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* Input — form_onlyモード不在時は非表示 */}
      {absentModeConfig.absentMode === 'form_only' && !supportStatus?.available && (showInquiryForm || inquirySubmitted) ? (
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '13px'
        }}>
          {inquirySubmitted ? 'お問い合わせを受け付けました。担当者より回答いたします。' : '上記フォームからお問い合わせください'}
        </div>
      ) : (
      <div style={{
        padding: '20px',
        background: '#ffffff',
        borderTop: '1px solid #e5e7eb'
      }}>
        {/* Pending File Preview */}
        {pendingFile && (
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: '#f3f4f6',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <span style={{ fontSize: '14px', color: '#374151' }}>
                {pendingFile.originalName}
              </span>
            </div>
            <button
              onClick={() => setPendingFile(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px'
              }}
              title="ファイルを削除"
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Recording Status */}
        {(isRecording || recordedAudio) && (
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: isRecording ? '#fef3c7' : '#dcfce7',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isRecording ? (
                <>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: '#ef4444',
                    borderRadius: '50%',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                  <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
                    録音中 {formatRecordingTime(recordingDuration)}
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '20px' }}>🎙️</span>
                  <span style={{ fontSize: '14px', color: '#166534', fontWeight: '600' }}>
                    音声録音完了 ({formatRecordingTime(recordingDuration)})
                  </span>
                </>
              )}
            </div>
            {!isRecording && recordedAudio && (
              <button
                onClick={cancelRecording}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                title="録音を削除"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Voice Recording Button */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!isConnected || !!recordedAudio}
            style={{
              background: isRecording ? '#ef4444' : '#f3f4f6',
              border: 'none',
              color: isRecording ? 'white' : '#6b7280',
              padding: '12px',
              borderRadius: '50%',
              cursor: (isConnected && !recordedAudio) ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              opacity: (isConnected && !recordedAudio) ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (isConnected && !recordedAudio) {
                (e.target as HTMLElement).style.background = isRecording ? '#dc2626' : '#e5e7eb';
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = isRecording ? '#ef4444' : '#f3f4f6';
            }}
            title={isRecording ? "録音を停止" : "音声を録音"}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isRecording ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>
          
          {/* File Upload Button */}
          <button
            onClick={() => setShowFileUpload(!showFileUpload)}
            disabled={!isConnected}
            style={{
              background: '#f3f4f6',
              border: 'none',
              color: '#6b7280',
              padding: '12px',
              borderRadius: '50%',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              opacity: isConnected ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (isConnected) {
                (e.target as HTMLElement).style.background = '#e5e7eb';
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = '#f3f4f6';
            }}
            title="ファイルを添付"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>
          
          <input
            type="text"
            value={inputValue}
            onInput={handleInputChange}
            onKeyDown={handleKeyPress}
            onCompositionEnd={handleCompositionEnd}
            placeholder="メッセージを入力してください..."
            disabled={false}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '24px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease',
              background: '#ffffff',
              fontFamily: 'inherit'
            }}
            onFocus={(e) => {
              (e.target as HTMLElement).style.borderColor = getModeColor();
              (e.target as HTMLElement).style.boxShadow = `0 0 0 3px ${getModeColor()}20`;
            }}
            onBlur={handleInputBlur}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() && !pendingFile && !recordedAudio}
            style={{
              background: (inputValue.trim() || pendingFile || recordedAudio) ? getModeColor() : '#e5e7eb',
              color: (inputValue.trim() || pendingFile || recordedAudio) ? 'white' : '#9ca3af',
              border: 'none',
              padding: '12px',
              borderRadius: '50%',
              cursor: (inputValue.trim() || pendingFile || recordedAudio) ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              if (inputValue.trim() || pendingFile || recordedAudio) {
                (e.target as HTMLElement).style.transform = 'scale(1.05)';
                (e.target as HTMLElement).style.background = getModeColor() + 'DD';
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.transform = 'scale(1)';
              if (inputValue.trim() || pendingFile || recordedAudio) {
                (e.target as HTMLElement).style.background = getModeColor();
              }
            }}
            title="送信"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>

      </div>
      )}

      {/* CSS for scrollbar and typing animation */}
      <style>{`
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .typing-dots {
          display: flex;
          gap: 2px;
        }

        .typing-dots span {
          animation: typing 1.4s infinite;
          font-size: 8px;
        }

        .typing-dots span:nth-child(1) { animation-delay: 0s; }
        .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .typing-dots span:nth-child(3) { animation-delay: 0.4s; }

        @keyframes typing {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
} 